import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { StreamChat, Event } from "stream-chat";
import { Chat } from "stream-chat-react";
import axios from "axios";
import Cookies from "js-cookie";
import { UserContext } from "./UserContext";

import "stream-chat-react/dist/css/v2/index.css";

interface StreamChatContextType {
  client: StreamChat | null;
  isConnected: boolean;
  unreadCount: number;
}

export const StreamChatContext = createContext<StreamChatContextType>({
  client: null,
  isConnected: false,
  unreadCount: 0,
});

export function StreamChatProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user } = useContext(UserContext);
  const [client, setClient] = useState<StreamChat | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Calculate total unread count from all channels
  const calculateUnreadCount = useCallback(async (chatClient: StreamChat) => {
    try {
      const channels = await chatClient.queryChannels(
        { members: { $in: [chatClient.userID || ""] } },
        { last_message_at: -1 },
        { limit: 30 },
      );

      const totalUnread = channels.reduce((count, channel) => {
        return count + (channel.countUnread() || 0);
      }, 0);

      setUnreadCount(totalUnread);
    } catch (error) {
      console.error("Error calculating unread count:", error);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      if (client) {
        client.disconnectUser();
        setClient(null);
        setIsConnected(false);
        setUnreadCount(0);
      }
      return;
    }

    const initChat = async () => {
      try {
        const token = Cookies.get("authToken");
        const response = await axios.get(`/api/chat/token?userId=${user.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success) {
          const chatClient = StreamChat.getInstance(response.data.apiKey);

          await chatClient.connectUser(
            {
              id: response.data.userId,
              name: user.name,
              image: user.profilePicture || undefined,
            },
            response.data.token,
          );

          setClient(chatClient);
          setIsConnected(true);

          // Calculate initial unread count
          await calculateUnreadCount(chatClient);

          // Listen for new messages to update unread count
          chatClient.on("message.new", (event: Event) => {
            // Only count messages from others
            if (event.user?.id !== chatClient.userID) {
              calculateUnreadCount(chatClient);
            }
          });

          // Listen for read events to update unread count
          chatClient.on("message.read", () => {
            calculateUnreadCount(chatClient);
          });

          chatClient.on("notification.mark_read", () => {
            calculateUnreadCount(chatClient);
          });
        }
      } catch (error) {
        console.error("Failed to initialize Stream Chat:", error);
      }
    };

    initChat();

    return () => {
      if (client) {
        client.disconnectUser();
      }
    };
  }, [user?.id, calculateUnreadCount]);

  return (
    <StreamChatContext.Provider value={{ client, isConnected, unreadCount }}>
      {client ? (
        <Chat client={client} theme="str-chat__theme-light">
          {children}
        </Chat>
      ) : (
        children
      )}
    </StreamChatContext.Provider>
  );
}
